/* @flow */
import * as React from 'react';

import { List as VirtualList, CellMeasurer, CellMeasurerCache, AutoSizer } from 'react-virtualized';
import type { TranslatorProps } from 'react-i18next';
import { observable, reaction } from 'mobx';
import { translate } from 'react-i18next';
import { observer } from 'mobx-react';

import FormControlLabel from '@material-ui/core/FormControlLabel';
import IconButton from '@material-ui/core/IconButton';
import Switch from '@material-ui/core/Switch';
import Button from '@material-ui/core/Button';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';

import { ComponentWithInjectedProps } from 'utils/injectedProps';
import Loading from 'components/Loading/Loading';
import DateFilter from './DateFilter';
import ListFilter from './ListFilter';
import type {
  ConsolidatedFilterItem,
  AvailableFilter,
  AvailableFilterOption,
  UserSearchPreference,
  AvailableGroupedFilterOption,
} from 'tables/components/ReportView/types';

import actionDrawerStyles from 'views/ActionDrawer/ActionDrawer.module.scss';
import styles from './PageFilters.module.scss';

type VirtualFilterRowProps = {
  index: number,
  key: string,
  availableFilter: AvailableFilter,
  availableFilterOptions: AvailableFilterOption[],
  style?: any,
  localFilter?: ConsolidatedFilterItem,

  onFilterItemChange: (
    availableFilter: AvailableFilter,
    availableFilterOption: AvailableFilterOption,
    newValue: boolean | string
  ) => void,
};

@observer
class VirtualFilterRow extends React.Component<VirtualFilterRowProps> {
  render() {
    const {
      index,
      key,
      style,
      availableFilter,
      localFilter,
      availableFilterOptions,
      onFilterItemChange,
    } = this.props;

    const oddEven = index % 2 === 0 ? { backgroundColor: '#fafafa' } : { backgroundColor: 'white' };
    const newStyles = {
      ...style,
      ...oddEven,
    };

    const availableFilterOption = availableFilterOptions[index];

    return (
      <ListFilter
        key={key}
        style={newStyles}
        filter={availableFilter}
        option={availableFilterOption}
        checked={!!localFilter && localFilter.values.includes(availableFilterOption.value)}
        disabled={!!localFilter && localFilter.isFixed}
        onChange={newValue => onFilterItemChange(availableFilter, availableFilterOption, newValue)}
      />
    );
  }
}

export type PageFiltersProps = {
  isActive: boolean,
  filters: ConsolidatedFilterItem[],
  tableCodeName: string,
  availableFilters?: AvailableFilter[],
  loadingFilters?: boolean,
  selectedUserSearchPreference?: ?UserSearchPreference,
  userSearchPreferences?: UserSearchPreference[],

  onChangeFilters?: (filters?: ConsolidatedFilterItem[]) => void,
  onChangeUserSearchPreferenceId?: (newId?: string) => void,
  onRequestDrawerClose?: () => void,
  onRemoveUserSearchPreference?: (userSearchPreference: UserSearchPreference) => void,
};

type InjectedProps = TranslatorProps;

@translate()
@observer
export default class PageFilters extends ComponentWithInjectedProps<
  PageFiltersProps,
  {},
  InjectedProps
> {
  @observable openFilterGroups: Map<string, boolean> = new Map();
  @observable tab = 0;

  @observable localFilters: ConsolidatedFilterItem[] = [];

  virtualListRef = React.createRef();

  _disposers: Function[] = [];

  componentDidMount() {
    this._disposers.push(
      reaction(
        () => this.props.filters,
        () => {
          this.localFilters = this.props.filters;
        },
        { fireImmediately: true }
      )
    );

    this.handleUpdateRowCache();
  }

  componentWillUnmount() {
    for (const disposer of this._disposers) {
      disposer();
    }
  }

  handleUpdateRowCache() {
    this.virtualListRef.current = new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: 40,
    });
  }

  toggleGroup = (g: string) => {
    if (this.openFilterGroups.has(g)) {
      this.openFilterGroups.delete(g);
    } else {
      this.openFilterGroups.set(g, true);
    }
  };

  getMatchingLocalFilter = (key: string) =>
    this.localFilters.find(localFilter => localFilter.key === key);

  handleTabChange = (_e: any, newTab: number) => {
    this.tab = newTab;
  };

  handleClearButtonPress = () => {
    const { onChangeFilters, onChangeUserSearchPreferenceId } = this.props;

    if (onChangeUserSearchPreferenceId) {
      onChangeUserSearchPreferenceId();
    }

    if (onChangeFilters) {
      onChangeFilters();
    }
  };

  handleApplyButtonPress = () => {
    const { onChangeFilters } = this.props;

    if (onChangeFilters) {
      onChangeFilters(this.localFilters);
    }
  };

  handleUserSearchPreferenceToggle = (
    userSearchPreference: UserSearchPreference,
    newValue: boolean
  ) => {
    const { onChangeUserSearchPreferenceId } = this.props;

    if (onChangeUserSearchPreferenceId) {
      onChangeUserSearchPreferenceId(
        newValue && userSearchPreference ? userSearchPreference.id : undefined
      );
    }
  };

  handleFilterItemChange = (
    availableFilter: AvailableFilter,
    availableFilterOption: AvailableFilterOption,
    newValue: boolean | string
  ) => {
    const matchingLocalFilter = this.getMatchingLocalFilter(availableFilter.key);
    const localFilter: ConsolidatedFilterItem = matchingLocalFilter || {
      key: availableFilter.key,
      values: [],
      name: availableFilter.name,
      type: availableFilter.type,
    };

    if (typeof newValue === 'boolean') {
      if (newValue) {
        localFilter.values.push(availableFilterOption.value);
      } else {
        localFilter.values.splice(
          localFilter.values.findIndex(value => value === availableFilterOption.value),
          1
        );
      }
    } else {
      localFilter.values = [newValue];
    }

    if (localFilter.values.length === 0 && matchingLocalFilter) {
      this.localFilters.splice(
        this.localFilters.findIndex(_localFilter => _localFilter === localFilter),
        1
      );
    }

    if (matchingLocalFilter && matchingLocalFilter.type === 'date' && newValue === '') {
      this.localFilters.splice(
        this.localFilters.findIndex(_localFilter => _localFilter === localFilter),
        1
      );
    }

    if (!matchingLocalFilter) {
      this.localFilters.push(localFilter);
    }
  };

  renderTabHeader = () => {
    const { availableFilters, loadingFilters, t } = this.props;

    if (availableFilters?.length || loadingFilters) {
      return (
        <Tabs value={this.tab} className={styles['filter-tabs']} onChange={this.handleTabChange}>
          <Tab label={t('general:labels.all_filters')} />
          <Tab label={t('general:labels.my_filters')} />
        </Tabs>
      );
    }

    return <Tab label={t('general:labels.my_filters')} />;
  };

  renderDrawerActions = () => {
    const { t } = this.props;

    return (
      <div className={actionDrawerStyles['action-drawer-actions']}>
        <Button variant="contained" onClick={this.handleClearButtonPress}>
          {t('general:actions.clear')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={this.tab === 1}
          onClick={this.handleApplyButtonPress}
        >
          {t('general:actions.apply')}
        </Button>
      </div>
    );
  };

  renderFiltersBody = (availableFilter: AvailableFilter) => {
    const availableFilterOptions = availableFilter.filters.filter(
      itemFilter =>
        (typeof itemFilter.value !== 'undefined' && itemFilter !== null) ||
        availableFilter.type === 'date'
    );
    const availableGroupedFilterOptions = Object.keys(
      availableFilter.groupedFilters ? availableFilter.groupedFilters : {}
    );
    const localFilter = this.getMatchingLocalFilter(availableFilter.key);

    const cleanAvailableFilterOptions = availableFilterOptions.filter(filterChild => {
      if (filterChild.label !== '') {
        return filterChild;
      }
    });
    if (this.virtualListRef.current === null) {
      this.handleUpdateRowCache();
    }

    return cleanAvailableFilterOptions.length > 8 ? (
      <AutoSizer style={{ width: 265, height: 240 }}>
        {({ width, height }) => (
          <VirtualList
            height={height}
            className={styles['virtual-list']}
            width={width}
            rowCount={cleanAvailableFilterOptions.length}
            rowHeight={this.virtualListRef.current.rowHeight}
            rowRenderer={({ index, key, style, parent }) => {
              return (
                <CellMeasurer
                  key={key}
                  cache={this.virtualListRef.current}
                  parent={parent}
                  columnIndex={0}
                  rowIndex={index}
                >
                  <VirtualFilterRow
                    index={index}
                    key={key}
                    style={style}
                    availableFilter={availableFilter}
                    availableFilterOptions={cleanAvailableFilterOptions}
                    localFilter={localFilter}
                    onFilterItemChange={this.handleFilterItemChange}
                  />
                </CellMeasurer>
              );
            }}
          />
        )}
      </AutoSizer>
    ) : (
      <>
        {availableFilterOptions.map(availableFilterOption => {
          const disabled = !!localFilter && localFilter.isFixed;

          if (availableFilter.type === 'string' || availableFilter.type === 'boolean') {
            return (
              <ListFilter
                key={
                  availableFilterOption.id
                    ? availableFilterOption.value
                    : availableFilterOption.label
                }
                filter={availableFilter}
                option={availableFilterOption}
                checked={!!localFilter && localFilter.values.includes(availableFilterOption.value)}
                disabled={disabled}
                onChange={newValue =>
                  this.handleFilterItemChange(availableFilter, availableFilterOption, newValue)
                }
              />
            );
          }

          return (
            <DateFilter
              key={availableFilterOption.value}
              filter={availableFilter}
              option={availableFilterOption}
              value={(!!localFilter && localFilter.values[0]) || ''}
              disabled={disabled}
              onChange={newValue =>
                this.handleFilterItemChange(availableFilter, availableFilterOption, newValue)
              }
            />
          );
        })}
        {availableGroupedFilterOptions.length > 0 &&
          availableGroupedFilterOptions.map(groupName => {
            return this.renderGroupedFiltersBody(
              availableFilter.groupedFilters,
              groupName,
              availableFilter
            );
          })}
      </>
    );
  };

  renderGroupedFiltersBody = (groupedFilters, groupName, availableFilter) => {
    const groupedFilterValues = groupedFilters[groupName];
    const isExpanded = this.openFilterGroups.has(groupName);
    const isExpandedClass = isExpanded ? styles['is-expanded'] : '';
    const localFilter = this.getMatchingLocalFilter(availableFilter.key);
    const disabled = !!localFilter && localFilter.isFixed;

    return (
      <li key={groupName} className={`${styles['sub-filter-header']} ${isExpandedClass}'`}>
        <h4 onClick={() => this.toggleGroup(groupName)}>
          <span>{groupName}</span>
          <svg>
            <use xlinkHref={`#icon-chevron-${isExpanded ? 'down' : 'up'}`} />
          </svg>
        </h4>
        {isExpanded && (
          <ul>
            {groupedFilterValues.map((groupedFilter: AvailableGroupedFilterOption) => {
              return (
                <ListFilter
                  key={groupedFilter.value}
                  filter={availableFilter}
                  option={groupedFilter}
                  checked={!!localFilter && localFilter.values.includes(groupedFilter.value)}
                  disabled={disabled}
                  onChange={newValue =>
                    this.handleFilterItemChange(availableFilter, groupedFilter, newValue)
                  }
                />
              );
            })}
          </ul>
        )}
      </li>
    );
  };

  renderFilterGroups = (availableFilter: AvailableFilter) => {
    const { tableCodeName, t, i18n } = this.props;

    // For the future developers:
    // If you need to rename one of the Azure Data Filters,
    // e.g To rename the filter Company Name Id to Company Name in the assets module.
    // We need to update the corresponding file and the language of its key(company_name_id).
    // You will find a path like this src / i18n / general / en.json / report_view.assets.filter_labels.company_name_id

    const filterGroupNameKey = `report_view.${tableCodeName.toLowerCase()}.filter_labels.${
      availableFilter.key
    }`;

    const filterGroupName = i18n.exists(filterGroupNameKey)
      ? t(filterGroupNameKey)
      : availableFilter.name;
    const isExpanded = this.openFilterGroups.has(availableFilter.key);
    const isExpandedClass = isExpanded ? styles['is-expanded'] : '';
    const isGroupedFilter =
      Object.keys(availableFilter.groupedFilters ? availableFilter.groupedFilters : {}).length > 0;
    const groupFilterClass = isGroupedFilter ? styles['is-group'] : '';

    return (
      <li key={availableFilter.key} className={`${isExpandedClass} ${groupFilterClass}`}>
        <h3 onClick={() => this.toggleGroup(availableFilter.key)}>
          <span>{filterGroupName}</span>
          <svg>
            <use xlinkHref={`#icon-chevron-${isExpanded ? 'down' : 'up'}`} />
          </svg>
        </h3>
        <ul
          key={availableFilter.key}
          className={`${styles['filters-list']} ${
            availableFilter.type === 'date' ? styles['date-filter'] : ''
          }`}
        >
          {this.renderFiltersBody(availableFilter)}
        </ul>
      </li>
    );
  };

  renderFiltersTab = () => {
    // NOTE: Technically, `availableFilters` should always be defined here
    const { availableFilters, loadingFilters } = this.props;

    return (
      <div key="tabMainFilters" className={styles['filters-container']}>
        {!availableFilters?.length && loadingFilters ? (
          <div className={styles.tabLoadingWrapper}>
            <Loading variant="small" />
          </div>
        ) : (
          <ul className={styles['filters-dropdown']}>
            {(availableFilters || []).map(availableFilter => {
              return this.renderFilterGroups(availableFilter);
            })}
          </ul>
        )}
      </div>
    );
  };

  renderUserSearchPreferencesTab = () => {
    const {
      userSearchPreferences,
      selectedUserSearchPreference,
      onRemoveUserSearchPreference,
      t,
    } = this.props;

    return (
      <div key="tabMyFilters" className={styles['filters-container']}>
        <ul className={styles['filters-list']}>
          {userSearchPreferences?.length ? (
            userSearchPreferences
              .sort((a, b) => (b.name < a.name ? -1 : b.name === a.name ? 0 : 1))
              .map(userSearchPreference => (
                <li key={userSearchPreference.id}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={
                          selectedUserSearchPreference
                            ? selectedUserSearchPreference.id === userSearchPreference.id
                            : false
                        }
                        onChange={(e, newValue) =>
                          this.handleUserSearchPreferenceToggle(userSearchPreference, newValue)
                        }
                        disableRipple={true}
                        color="primary"
                      />
                    }
                    label={userSearchPreference.name}
                  />
                  <IconButton
                    className={styles.binIcon}
                    onClick={(e: SyntheticInputEvent<*>, newValue) => {
                      if (onRemoveUserSearchPreference) {
                        onRemoveUserSearchPreference(userSearchPreference);
                      }

                      e.stopPropagation();
                    }}
                  >
                    <svg>
                      <use xlinkHref="#icon-bin" />
                    </svg>
                  </IconButton>
                </li>
              ))
          ) : (
            <div className={styles.noItemsWrapper}>{t('general:no_search_preferences')}</div>
          )}
        </ul>
      </div>
    );
  };

  render() {
    const { isActive, onRequestDrawerClose } = this.props;
    const activeClass = isActive ? styles.active : '';

    return (
      <div className={`${styles['page-filters-wrapper']} ${activeClass}`}>
        <div className={styles['page-filters']}>
          <div className={styles['filter-header']}>
            <h2>Filter</h2>
            <IconButton className={styles['filter-drawer-close']} onClick={onRequestDrawerClose}>
              <svg className={styles['icon-close']}>
                <use xlinkHref="#icon-close" />
              </svg>
            </IconButton>
          </div>

          {this.renderTabHeader()}
          {[this.renderFiltersTab, this.renderUserSearchPreferencesTab][this.tab]()}

          {this.renderDrawerActions()}
        </div>
      </div>
    );
  }
}
